// utils/roomTypeHelper.js

/**
 * Helper functions to handle room types structure migration
 * Supports both old (channexMapping.roomTypes) and new (property.roomTypes) structures
 */

/**
 * Get room types from property, handling both old and new structures
 * @param {Object} property - The property object
 * @returns {Array} Array of room types
 */
export function getRoomTypes(property) {
  // Check new structure first
  if (property?.roomTypes && property.roomTypes.length > 0) {
    return property.roomTypes;
  }

  // Fallback to old structure for backward compatibility
  if (property?.channexMapping?.roomTypes) {
    return property.channexMapping.roomTypes;
  }

  return [];
}

/**
 * Get Channex room mapping for a specific room
 * @param {Object} property - The property object
 * @param {String} roomId - Internal room ID
 * @returns {Object|null} Channex mapping data with rate plans
 */
export function getChannexRoomMapping(property, roomId) {
  // Check new structure first
  if (property?.channexMapping?.roomMappings) {
    // roomMappings is a Map in backend but object in frontend JSON
    const mapping = property.channexMapping.roomMappings[roomId];
    if (mapping) {
      return {
        channexRoomTypeId: mapping.channexRoomTypeId,
        ratePlans: mapping.ratePlans || []
      };
    }
  }

  // Fallback to old structure
  if (property?.channexMapping?.roomTypes) {
    const oldRoom = property.channexMapping.roomTypes.find(rt =>
      rt.id === roomId || rt.channexId === roomId
    );
    if (oldRoom) {
      return {
        channexRoomTypeId: oldRoom.channexId || oldRoom.id,
        ratePlans: oldRoom.ratePlans || []
      };
    }
  }

  return null;
}

/**
 * Get all room types with their rate plans
 * Combines room types with their channel mappings
 * @param {Object} property - The property object
 * @returns {Array} Array of room types with rate plans
 */
export function getRoomTypesWithRatePlans(property) {
  const roomTypes = getRoomTypes(property);

  return roomTypes.map(room => {
    const mapping = getChannexRoomMapping(property, room.id);
    return {
      ...room,
      ratePlans: mapping?.ratePlans || room.ratePlans || []
    };
  });
}

/**
 * Find room by ID
 * @param {Object} property - The property object
 * @param {String} roomId - Room ID to find
 * @returns {Object|null} Room type object
 */
export function findRoomById(property, roomId) {
  const roomTypes = getRoomTypes(property);
  return roomTypes.find(rt => rt.id === roomId) || null;
}

/**
 * Find rate plan by IDs
 * @param {Object} property - The property object
 * @param {String} roomId - Room ID
 * @param {String} ratePlanId - Rate plan ID
 * @returns {Object|null} Rate plan object
 */
export function findRatePlan(property, roomId, ratePlanId) {
  const mapping = getChannexRoomMapping(property, roomId);
  if (mapping?.ratePlans) {
    return mapping.ratePlans.find(rp => rp.id === ratePlanId) || null;
  }

  // Fallback to old structure
  const room = findRoomById(property, roomId);
  if (room?.ratePlans) {
    return room.ratePlans.find(rp => rp.id === ratePlanId) || null;
  }

  return null;
}

/**
 * Get the effective room type ID for Channex operations (rates, availability, etc.)
 * For multi-unit properties, uses linkedRoomTypeId; for single-unit, uses roomTypes[0].id
 *
 * @param {Object} property - The property object
 * @returns {String|null} The room type ID to use for Channex operations
 */
export function getEffectiveRoomTypeId(property) {
  // Multi-unit: Use linkedRoomTypeId if set
  if (property?.channexMapping?.linkedRoomTypeId) {
    return property.channexMapping.linkedRoomTypeId;
  }

  // Single-unit: Fallback to roomTypes[0].id
  const roomTypes = getRoomTypes(property);
  if (roomTypes.length > 0) {
    return roomTypes[0].id;
  }

  return null;
}

/**
 * Get room mapping using the effective room type ID
 * Handles both single-unit and multi-unit properties
 *
 * @param {Object} property - The property object
 * @returns {Object|null} Channex mapping data with rate plans
 */
export function getEffectiveRoomMapping(property) {
  const effectiveRoomTypeId = getEffectiveRoomTypeId(property);

  if (!effectiveRoomTypeId) {
    return null;
  }

  // Look up in roomMappings using the effective ID
  if (property?.channexMapping?.roomMappings) {
    const mapping = property.channexMapping.roomMappings[effectiveRoomTypeId];

    if (mapping) {
      const result = {
        roomTypeId: effectiveRoomTypeId,
        channexRoomTypeId: mapping.channexRoomTypeId || effectiveRoomTypeId,
        ratePlans: mapping.ratePlans || []
      };
      return result;
    }
  }

  // Fallback: Try using roomTypes[0].id for older structures
  const roomTypes = getRoomTypes(property);
  if (roomTypes.length > 0) {
    const fallbackMapping = getChannexRoomMapping(property, roomTypes[0].id);
    if (fallbackMapping) {
      const result = {
        roomTypeId: roomTypes[0].id,
        ...fallbackMapping
      };
      return result;
    }
  }

  return null;
}